import { COLORS, SURFACES } from '../design/tokens';
import { statusTint } from './palette';
import { createRandom, hashString, toDataUri } from './seed';

const WIDTH = 480;
const HEIGHT = 300;

export interface CoverSeed {
  readonly title: string;
  readonly status: string;
}

/**
 * A generated project cover: grid, topographic arcs, node scatter, tinted by status.
 *
 * Used when a project has no uploaded image. § C is explicit that a project without an
 * image must never render an empty box.
 *
 * **The seed is `title + status`, so renaming a project changes its artwork.** The spec
 * says "the same project always yields the same artwork"; strictly it is the same
 * title+status that does. That is acceptable — but it is why the status crossing the
 * wire is the enum NAME and not display text. Change `"InProduction"` to
 * `"In production"` and every cover silently regenerates.
 */
export function generateCover({ title, status }: CoverSeed): string {
  const random = createRandom(hashString(`${title}|${status}`));
  const tint = statusTint(status);
  const parts: string[] = [];

  parts.push(`<rect width="${WIDTH}" height="${HEIGHT}" fill="${SURFACES.well}"/>`);

  // One soft tinted bloom, placed from the seed.
  const bloomX = (60 + random() * 360).toFixed(0);
  const bloomY = (40 + random() * 220).toFixed(0);
  const bloomR = (90 + random() * 70).toFixed(0);
  parts.push(
    `<circle cx="${bloomX}" cy="${bloomY}" r="${bloomR}" fill="${tint}" opacity="0.10"/>`,
  );

  // Measuring grid.
  for (let x = 0; x <= WIDTH; x += 24) {
    parts.push(
      `<line x1="${x}" y1="0" x2="${x}" y2="${HEIGHT}" stroke="${COLORS.text}" stroke-width="0.4" opacity="0.05"/>`,
    );
  }
  for (let y = 0; y <= HEIGHT; y += 24) {
    parts.push(
      `<line x1="0" y1="${y}" x2="${WIDTH}" y2="${y}" stroke="${COLORS.text}" stroke-width="0.4" opacity="0.05"/>`,
    );
  }

  // Topographic arcs.
  for (let index = 0; index < 7; index += 1) {
    const centreY = 40 + index * 34 + random() * 12;
    const amplitude = 14 + random() * 26;
    const strokeWidth = (0.7 + random() * 0.9).toFixed(1);
    const opacity = (0.1 + random() * 0.22).toFixed(2);

    parts.push(
      `<path d="M -20 ${centreY.toFixed(0)} Q ${WIDTH * 0.25} ${(centreY - amplitude).toFixed(0)} ` +
        `${WIDTH * 0.5} ${centreY.toFixed(0)} T ${WIDTH + 20} ${centreY.toFixed(0)}" ` +
        `fill="none" stroke="${tint}" stroke-width="${strokeWidth}" opacity="${opacity}"/>`,
    );
  }

  // Node scatter.
  for (let index = 0; index < 9; index += 1) {
    const cx = (30 + random() * (WIDTH - 60)).toFixed(1);
    const cy = (30 + random() * (HEIGHT - 60)).toFixed(1);
    const r = (1.4 + random() * 2.4).toFixed(1);
    const opacity = (0.25 + random() * 0.5).toFixed(2);

    parts.push(
      `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${tint}" opacity="${opacity}"/>`,
    );
  }

  parts.push(
    `<rect x="0.5" y="0.5" width="${WIDTH - 1}" height="${HEIGHT - 1}" fill="none" stroke="${tint}" opacity="0.18"/>`,
  );

  // No <text>, deliberately.
  //
  // An SVG loaded as an image is an isolated document: it cannot reach the host page's
  // @font-face, and it cannot fetch anything. The prototype's monogram would silently
  // render in whatever fallback sans the platform picks, which looks like a bug rather
  // than a choice. Inlining Inter as base64 into every data URI is the only fix and it
  // is far too expensive for decorative artwork.
  return toDataUri(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${WIDTH} ${HEIGHT}" ` +
      `width="${WIDTH}" height="${HEIGHT}" role="presentation">${parts.join('')}</svg>`,
  );
}
